
import { useRef, useEffect, useState } from 'react';

const STEPS = [
  {
    num: '01',
    title: 'Discovery & Strategia',
    desc: 'Dogłębna analiza Twojego biznesu, konkurencji i grupy docelowej. Definiujemy cele i KPI.',
    time: '1–2 tygodnie',
    tags: ['Brief', 'Analiza', 'Strategia'],
    color: '#00d4ff',
    icon: 'ri-search-eye-line',
  },
  {
    num: '02',
    title: 'Projektowanie UX/UI',
    desc: 'Tworzymy wireframes i prototypy. Design system dopasowany do Twojej marki.',
    time: '2–3 tygodnie',
    tags: ['Wireframes', 'Prototyp', 'Design System'],
    color: '#00ff88',
    icon: 'ri-pencil-ruler-2-line',
  },
  {
    num: '03',
    title: 'Rozwój & Integracje',
    desc: 'Kodujemy stronę z naciskiem na performance i SEO. Integrujemy narzędzia i systemy.',
    time: '3–5 tygodni',
    tags: ['Frontend', 'Backend', 'Integracje'],
    color: '#ff6b9d',
    icon: 'ri-code-s-slash-line',
  },
  {
    num: '04',
    title: 'Testy & Optymalizacja',
    desc: 'Kompleksowe testy funkcjonalne, wydajnościowe i UX. Optymalizacja Core Web Vitals.',
    time: '1–2 tygodnie',
    tags: ['QA Testing', 'Performance', 'SEO Audit'],
    color: '#00d4ff',
    icon: 'ri-bug-line',
  },
  {
    num: '05',
    title: 'Wdrożenie & Wsparcie',
    desc: 'Publikacja strony, szkolenie zespołu i ciągłe wsparcie techniczne oraz rozwój.',
    time: 'Ongoing',
    tags: ['Launch', 'Szkolenie', 'Support 24/7'],
    color: '#00ff88',
    icon: 'ri-rocket-line',
  },
];

function StepCard({ step, index }: { step: typeof STEPS[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative flex gap-6 md:gap-10"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-30px)',
        transition: `opacity 0.6s ease ${index * 0.15}s, transform 0.6s ease ${index * 0.15}s`,
      }}
    >
      {/* Left: number + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center font-black text-lg relative z-10"
          style={{
            background: `${step.color}15`,
            border: `1.5px solid ${step.color}40`,
            color: step.color,
          }}
        >
          {step.num}
        </div>
        {index < STEPS.length - 1 && (
          <div
            className="w-px flex-1 mt-3"
            style={{ background: `linear-gradient(to bottom, ${step.color}40, transparent)`, minHeight: '60px' }}
          />
        )}
      </div>

      {/* Right: content */}
      <div className="pb-12 flex-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`${step.icon} text-xl`} style={{ color: step.color }} />
          </div>
          <h3 className="text-xl font-bold text-white">{step.title}</h3>
          <span
            className="ml-auto text-xs font-semibold px-3 py-1 rounded-full"
            style={{ background: `${step.color}15`, color: step.color, border: `1px solid ${step.color}30` }}
          >
            {step.time}
          </span>
        </div>
        <p className="text-white/50 text-sm leading-relaxed mb-4">{step.desc}</p>
        <div className="flex flex-wrap gap-2">
          {step.tags.map((tag) => (
            <span key={tag} className="px-2.5 py-1 rounded-full text-xs text-white/40 border border-white/10">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProcessSection() {
  return (
    <section className="py-32 bg-[#060606] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left sticky */}
          <div className="lg:sticky lg:top-32">
            <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">Jak pracujemy</p>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
              Przejrzysty proces,<br />
              <span className="text-white/40">przewidywalne rezultaty</span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-8">
              Każdy projekt realizujemy według sprawdzonego procesu, który gwarantuje terminowość, jakość i realne wyniki biznesowe.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="text-3xl font-black text-[#00d4ff] mb-1">7–12</div>
                <div className="text-white/40 text-xs">tygodni do launchu</div>
              </div>
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="text-3xl font-black text-[#00ff88] mb-1">100%</div>
                <div className="text-white/40 text-xs">projektów na czas</div>
              </div>
            </div>
          </div>

          {/* Right: steps */}
          <div>
            {STEPS.map((step, i) => (
              <StepCard key={step.num} step={step} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
